import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useEffect, useState } from 'react';
import { api } from 'src/api';
import { getSuggestionKey, isAllocatedTableWritten } from './suggestionModel';

// Called straight through the api layer rather than the store action of the same name: that action
// replaces the allocation tables the Allocations page works from, and this is a different bill's.
const NO_OPTIONS = {
  cancelToken: undefined
};

/** How long the charges take to be worked out, at the outside, once the table has been approved. */
const RETRY_DELAY_MS = 2000;
const MAX_RETRIES = 5;
async function fetchTable(_ref) {
  let billingCycleId = _ref.billingCycleId,
    propertyId = _ref.propertyId,
    utilityId = _ref.utilityId;
  try {
    var _resp$response;
    const resp = await api.Allocations.getAllocationTableAggregate({
      billingCycleId,
      propertyId,
      utilityId
    }, NO_OPTIONS);
    const table = ((resp === null || resp === void 0 || (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.utilityTables) || []).find(item => item.utilityId === utilityId);
    return table ? [getSuggestionKey(propertyId, utilityId), table] : null;
  } catch (_unused) {
    // The card falls back to what it was showing. The allocation is made either way — this only
    // decides whether its table can be read yet.
    return null;
  }
}

/**
 * The saved allocation tables for services that have one, keyed by property and utility. Given the
 * services the allocation service has finished calculating — each with the billing cycle it filed
 * the bill under — this fetches what it actually allocated, which is what the card shows in place
 * of the suggestion once a service is allocated.
 *
 * A table approved a moment ago can be read back before its charges have been written, so a table
 * that comes back unwritten is asked for again rather than shown. Failing that, the card keeps
 * showing the suggestion, which is the same figures by another name.
 */
export function useAllocatedTables(services) {
  const _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    tables = _useState2[0],
    setTables = _useState2[1];
  // The addresses are the dependency; a re-render naming the same ones changes nothing.
  const signature = JSON.stringify((services || []).filter(service => (service === null || service === void 0 ? void 0 : service.propertyId) && (service === null || service === void 0 ? void 0 : service.utilityId) && (service === null || service === void 0 ? void 0 : service.billingCycleId)).map(_ref2 => {
    let billingCycleId = _ref2.billingCycleId,
      propertyId = _ref2.propertyId,
      utilityId = _ref2.utilityId;
    return {
      billingCycleId,
      propertyId,
      utilityId
    };
  }).sort((a, b) => "".concat(a.propertyId).concat(a.utilityId).localeCompare("".concat(b.propertyId).concat(b.utilityId))));
  useEffect(() => {
    const wanted = JSON.parse(signature);
    if (!wanted.length) {
      setTables({});
      return undefined;
    }
    let isActive = true;
    let timer;
    const written = new Map();
    const attempt = async (pending, retriesLeft) => {
      const fetched = await Promise.all(pending.map(fetchTable));
      if (!isActive) return;
      fetched.filter(entry => entry && isAllocatedTableWritten(entry[1])).forEach(_ref3 => {
        let _ref4 = _slicedToArray(_ref3, 2),
          key = _ref4[0],
          table = _ref4[1];
        return written.set(key, table);
      });
      setTables(Object.fromEntries(written));
      const stillPending = pending.filter(address => !written.has(getSuggestionKey(address.propertyId, address.utilityId)));
      if (!stillPending.length || retriesLeft <= 0) return;
      timer = setTimeout(() => attempt(stillPending, retriesLeft - 1), RETRY_DELAY_MS);
    };
    attempt(wanted, MAX_RETRIES);
    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [signature]);
  return tables;
}